import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Image, TouchableOpacity, Platform, Easing } from 'react-native';

const ConnectTabButton = ({ focused, onPress, style }) => {
    const rotateAnim = useRef(new Animated.Value(0)).current;

    // Wavy animations
    const waveAnim1 = useRef(new Animated.Value(0)).current;
    const waveAnim2 = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        // Continuous rotation regardless of focus
        Animated.loop(
            Animated.timing(rotateAnim, {
                toValue: 1,
                duration: 12000,
                easing: Easing.linear,
                useNativeDriver: true,
            })
        ).start();
    }, [rotateAnim]);

    useEffect(() => {
        if (focused) {
            Animated.parallel([
                Animated.loop(
                    Animated.timing(waveAnim1, {
                        toValue: 1,
                        duration: 3000,
                        easing: Easing.linear,
                        useNativeDriver: true,
                    })
                ),
                Animated.loop(
                    Animated.timing(waveAnim2, {
                        toValue: 1,
                        duration: 4000,
                        easing: Easing.linear,
                        useNativeDriver: true,
                    })
                )
            ]).start();
        } else {
            waveAnim1.setValue(0);
            waveAnim2.setValue(0);
        }
    }, [focused, waveAnim1, waveAnim2]);

    const rotation = rotateAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
    });

    const waveRotate1 = waveAnim1.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
    });

    const waveRotate2 = waveAnim2.interpolate({
        inputRange: [0, 1],
        outputRange: ['360deg', '0deg'],
    });

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.9}
            style={[style, styles.container]}
        >
            <View style={styles.mainButton}>
                {/* Rotating Brush Icon */}
                <Animated.View style={[
                    styles.iconWrapper,
                    { transform: [{ rotate: rotation }] }
                ]}>
                    <Image
                        source={require('../../assets/connect-icon.png')}
                        style={styles.brushCircle}
                        resizeMode="contain"
                    />
                </Animated.View>

                {/* Glassy Wavy Inner Animation */}
                {focused && (
                    <View style={styles.glassContainer}>
                        <Animated.View style={[
                            styles.wave,
                            { transform: [{ rotate: waveRotate1 }] }
                        ]} />
                        <Animated.View style={[
                            styles.wave,
                            styles.wave2,
                            { transform: [{ rotate: waveRotate2 }] }
                        ]} />
                    </View>
                )}

                {/* Internal Content */}
                <View style={styles.internalContainer}>
                    <Text style={[
                        styles.connectText,
                        { color: focused ? '#6366F1' : '#9CA3AF' }
                    ]}>
                        Connect
                    </Text>
                </View>
            </View>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    container: {
        top: -8,
        justifyContent: 'center',
        alignItems: 'center',
        // Removed fixed width for even spacing
        height: 55,
    },
    mainButton: {
        width: 52,
        height: 52,
        borderRadius: 26,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
        ...Platform.select({
            ios: {
                shadowColor: '#6366F1',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.3,
                shadowRadius: 8,
            },
            android: {
                elevation: 10,
            },
        }),
    },
    iconWrapper: {
        position: 'absolute',
        width: 48,
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
    },
    brushCircle: {
        width: 44,
        height: 44,
    },
    glassContainer: {
        position: 'absolute',
        width: 36,
        height: 36,
        borderRadius: 18,
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(249, 161, 159, 0.05)',
    },
    wave: {
        position: 'absolute',
        width: 44,
        height: 44,
        backgroundColor: 'rgba(255, 178, 178, 0.12)',
        borderRadius: 18,
        top: 4,
    },
    wave2: {
        width: 46,
        height: 46,
        backgroundColor: 'rgba(125, 214, 255, 0.25)',
        borderRadius: 20,
        top: 6,
    },
    internalContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2, // Ensure text is above the pulse
    },
    connectText: {
        fontSize: 9,
        fontWeight: '900',
        letterSpacing: 0.4,
        textAlign: 'center',
    },
});

export default ConnectTabButton;
